import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Search, Filter, Sparkles, Wand2,
  FileText, Brain, X, Tags, ChevronDown, ChevronUp, Image, FileUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = ['All', 'basic', 'comps_ready', 'advanced'];
const TIER_LABELS = { basic: 'Basic Practice', comps_ready: 'Comps Ready', advanced: 'Advanced' };
const SOURCES = ['All', 'manual', 'ai_content', 'ai_prompt', 'ai_image', 'ai_document'];
const AI_PROVIDERS = [
  { value: 'openai', label: 'OpenAI', models: ['gpt-5.4', 'gpt-4o', 'gpt-4o-mini'] },
  { value: 'anthropic', label: 'Claude (Anthropic)', models: ['claude-sonnet-5', 'claude-sonnet-4-6', 'claude-opus-4-8'] },
  { value: 'gemini', label: 'Gemini (Google)', models: ['gemini-2.5-pro', 'gemini-2.0-flash'] }
];

export default function QuestionBankManager() {
  const [activeView, setActiveView] = useState('pool'); // 'pool', 'ai-content', 'ai-prompt', 'ai-image'
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [tests, setTests] = useState([]);
  
  // Test Categories Management
  const [allCategories, setAllCategories] = useState([]);
  const [categoriesByTier, setCategoriesByTier] = useState({
    basic: [],
    comps_ready: [],
    advanced: []
  });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('All');
  const [sectionFilter, setSectionFilter] = useState('All');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('All');
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  
  // AI Wizard States
  const [contentWizard, setContentWizard] = useState({
    content: '',
    tier: 'basic',
    section: '',
    provider: 'openai',
    model: 'gpt-5',
    num_questions: 5,
    citation: '',  // Optional citation field
    guidance: ''   // Optional guidance to direct generation
  });
  
  const [promptWizard, setPromptWizard] = useState({
    prompt: '',
    tier: 'basic',
    section: '',
    provider: 'openai',
    model: 'gpt-5',
    num_questions: 5,
    quiz_type: 'questions',
    guidance: ''
  });
  
  // Document wizard state (generate from uploaded DOC/PDF study guide)
  const [documentWizard, setDocumentWizard] = useState({
    tier: 'basic',
    section: '',
    provider: 'anthropic',
    model: 'claude-sonnet-5',
    num_questions: 10,
    citation: '',
    guidance: '',
    count_mode: 'total',
    create_test: false,
    test_title: '',
    passing_score: 70
  });
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docJob, setDocJob] = useState(null);  // { processed, total } progress
  
  // Image wizard state
  const [imageWizard, setImageWizard] = useState({
    tier: 'basic',
    section: '',
    model: 'gemini-2.5-pro',
    num_questions: 5
  });
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showTestForm, setShowTestForm] = useState(false);
  const [testForm, setTestForm] = useState({
    title: '',
    description: '',
    tier: 'basic',
    category: 'acupuncture',
    question_count: 10,
    passing_score: 70
  });

  // Selection state for adding questions to test
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showAddToTestModal, setShowAddToTestModal] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState('');
  
  // Edit question state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  useEffect(() => {
    loadQuestions();
    loadTests();
    loadCategories();
  }, []);

  useEffect(() => {
    filterQuestions();
  }, [questions, searchTerm, tierFilter, sectionFilter, sourceFilter, dateFilter]);
  
  // Initialize section when tier changes
  useEffect(() => {
    if (tierFilter !== 'All' && categoriesByTier[tierFilter]?.length > 0) {
      const firstSection = categoriesByTier[tierFilter][0].value;
      if (sectionFilter === 'All') {
        setSectionFilter('All');
      }
    }
  }, [tierFilter, categoriesByTier]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/test-categories`, {
        withCredentials: true
      });
      setAllCategories(response.data);
      
      // Organize categories by tier
      const byTier = {
        basic: [],
        comps_ready: [],
        advanced: []
      };
      
      response.data.forEach(cat => {
        if (cat.enabled && byTier[cat.tier]) {
          byTier[cat.tier].push({
            value: cat.slug,
            label: cat.name
          });
        }
      });
      
      setCategoriesByTier(byTier);
      
      // Set initial section for wizards
      if (byTier.basic.length > 0) {
        setContentWizard(prev => ({ ...prev, section: byTier.basic[0].value }));
        setPromptWizard(prev => ({ ...prev, section: byTier.basic[0].value }));
        setImageWizard(prev => ({ ...prev, section: byTier.basic[0].value }));
        setDocumentWizard(prev => ({ ...prev, section: byTier.basic[0].value }));
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load test categories');
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await axios.get(`${API}/admin/question-pool`, {
        withCredentials: true
      });
      setQuestions(response.data);
    } catch (error) {
      console.error('Failed to load questions:', error);
    }
  };

  const loadTests = async () => {
    try {
      const response = await axios.get(`${API}/tests`);
      setTests(response.data);
    } catch (error) {
      console.error('Failed to load tests:', error);
    }
  };

  const filterQuestions = () => {
    let filtered = [...questions];

    if (searchTerm) {
      filtered = filtered.filter(q =>
        q.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by tier (maps to category in database for backwards compatibility)
    if (tierFilter !== 'All') {
      // Get all sections for this tier
      const tierSections = categoriesByTier[tierFilter] || [];
      const tierSectionSlugs = tierSections.map(s => s.value);
      
      filtered = filtered.filter(q => tierSectionSlugs.includes(q.category));
    }

    // Filter by section (maps to category slug in database)
    if (sectionFilter !== 'All') {
      filtered = filtered.filter(q => q.category === sectionFilter);
    }

    if (sourceFilter !== 'All') {
      filtered = filtered.filter(q => q.source === sourceFilter);
    }

    // Filter by creation date
    if (dateFilter !== 'All') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      filtered = filtered.filter(q => {
        const createdDate = new Date(q.created_at);
        switch (dateFilter) {
          case 'Today':
            return createdDate >= today;
          case 'Yesterday':
            return createdDate >= yesterday && createdDate < today;
          case 'This Week':
            return createdDate >= weekAgo;
          case 'This Month':
            return createdDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    setFilteredQuestions(filtered);
  };
  
  // Helper to get section label
  const getSectionLabel = (tier, section) => {
    const tierCategories = categoriesByTier[tier] || [];
    const found = tierCategories.find(cat => cat.value === section);
    return found ? found.label : section;
  };
  
  // Helper to get section label from slug (when we don't know tier)
  const getSectionLabelFromSlug = (slug) => {
    for (const tier of ['basic', 'comps_ready', 'advanced']) {
      const found = categoriesByTier[tier]?.find(cat => cat.value === slug);
      if (found) return found.label;
    }
    return slug; // Fallback to slug if not found
  };

  const handleAIGenerateFromContent = async () => {
    if (!contentWizard.content.trim()) {
      toast.error('Please enter content');
      return;
    }
    
    if (!contentWizard.section) {
      toast.error('Please select a section');
      return;
    }
    
    setAiGenerating(true);
    try {
      // Map to backend format: category = section slug, level = tier label
      const requestData = {
        content: contentWizard.content,
        category: contentWizard.section,  // section slug
        level: TIER_LABELS[contentWizard.tier] || contentWizard.tier,  // tier label
        provider: contentWizard.provider,
        model: contentWizard.model,
        num_questions: contentWizard.num_questions,
        citation: contentWizard.citation || null,  // Optional citation
        guidance: contentWizard.guidance || null
      };
      
      const response = await axios.post(`${API}/admin/ai-generate-from-content`, requestData, {
        withCredentials: true
      });
      toast.success(`Generated ${response.data.count} questions!`);
      setActiveView('pool');
      loadQuestions();
      setContentWizard({ ...contentWizard, content: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAIGenerateFromPrompt = async () => {
    if (!promptWizard.prompt.trim()) {
      toast.error('Please enter a topic/prompt');
      return;
    }
    
    if (!promptWizard.section) {
      toast.error('Please select a section');
      return;
    }
    
    setAiGenerating(true);
    try {
      // Map to backend format: category = section slug, level = tier label
      const requestData = {
        prompt: promptWizard.prompt,
        category: promptWizard.section,  // section slug
        level: TIER_LABELS[promptWizard.tier] || promptWizard.tier,  // tier label
        provider: promptWizard.provider,
        model: promptWizard.model,
        num_questions: promptWizard.num_questions,
        quiz_type: promptWizard.quiz_type,
        guidance: promptWizard.guidance || null
      };
      
      const response = await axios.post(`${API}/admin/ai-generate-from-prompt`, requestData, {
        withCredentials: true
      });
      toast.success(`Generated ${response.data.count} questions!`);
      setActiveView('pool');
      loadQuestions();
      setPromptWizard({ ...promptWizard, prompt: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    await processImageFile(file);
  };
  
  const handleImagePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (let item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          await processImageFile(file);
        }
        break;
      }
    }
  };
  
  const processImageFile = async (file) => {
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (PNG, JPG, JPEG, or WebP)');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 10MB');
      return;
    }
    
    setImageUploading(true);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setSelectedImage(file);
      setImageUploading(false);
      toast.success('✓ Image ready for generation');
    };
    reader.onerror = () => {
      toast.error('Failed to load image');
      setImageUploading(false);
    };
    reader.readAsDataURL(file);
  };
  
  const handleClearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleAIGenerateFromImage = async () => {
    if (!selectedImage) {
      toast.error('Please select an image first');
      return;
    }
    
    if (!imageWizard.section) {
      toast.error('Please select a section');
      return;
    }
    
    setAiGenerating(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedImage);
      formData.append('category', imageWizard.section);  // section slug
      formData.append('level', TIER_LABELS[imageWizard.tier] || imageWizard.tier);  // tier label
      formData.append('model', imageWizard.model);
      formData.append('num_questions', imageWizard.num_questions.toString());
      
      const response = await axios.post(`${API}/admin/ai-generate-from-image`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Generated ${response.data.count} questions from image!`);
      setActiveView('pool');
      loadQuestions();
      handleClearImage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI generation from image failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAIGenerateFromDocument = async () => {
    if (!selectedDoc) {
      toast.error('Please select a .docx or .pdf file');
      return;
    }
    if (!documentWizard.section) {
      toast.error('Please select a section');
      return;
    }
    if (documentWizard.create_test && !documentWizard.test_title.trim()) {
      toast.error('Please enter a test title');
      return;
    }
    setAiGenerating(true);
    setDocJob({ processed: 0, total: 0 });
    try {
      const formData = new FormData();
      formData.append('file', selectedDoc);
      formData.append('category', documentWizard.section);
      formData.append('level', TIER_LABELS[documentWizard.tier] || documentWizard.tier);
      formData.append('provider', documentWizard.provider);
      formData.append('model', documentWizard.model);
      formData.append('num_questions', documentWizard.num_questions.toString());
      formData.append('guidance', documentWizard.guidance || '');
      formData.append('citation', documentWizard.citation || '');
      formData.append('count_mode', documentWizard.count_mode);

      const endpoint = documentWizard.create_test
        ? `${API}/admin/study-guide-to-test/start`
        : `${API}/admin/ai-generate-from-document/start`;
      if (documentWizard.create_test) {
        formData.append('title', documentWizard.test_title);
        formData.append('passing_score', documentWizard.passing_score.toString());
      }

      const startRes = await axios.post(endpoint, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const jobId = startRes.data.job_id;
      setDocJob({ processed: 0, total: startRes.data.total_chunks });

      let finished = false;
      while (!finished) {
        await new Promise(r => setTimeout(r, 1500));
        const st = await axios.get(`${API}/admin/ai-generate-from-document/status/${jobId}`, { withCredentials: true });
        setDocJob({ processed: st.data.processed_chunks, total: st.data.total_chunks });
        if (st.data.status === 'done') {
          finished = true;
          if (documentWizard.create_test && st.data.test_id) {
            toast.success(`Test "${st.data.test_title}" created with ${st.data.created_count} questions! Opening it in Test Bank…`);
            loadQuestions();
            setTimeout(() => {
              window.location.href = `/admin?tab=test-bank&highlightTest=${st.data.test_id}`;
            }, 1200);
          } else {
            toast.success(`Generated ${st.data.created_count} questions from your study guide!`);
            setActiveView('pool');
            loadQuestions();
            loadTests();
            setSelectedDoc(null);
          }
        } else if (st.data.status === 'error') {
          finished = true;
          toast.error(st.data.error || 'AI generation from document failed');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI generation from document failed');
    } finally {
      setAiGenerating(false);
      setDocJob(null);
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    
    try {
      await axios.delete(`${API}/admin/question-pool/${id}`, {
        withCredentials: true
      });
      toast.success('Question deleted');
      loadQuestions();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };
  
  const handleEditQuestion = (question) => {
    setEditingQuestion({
      ...question,
      tags: question.tags || []
    });
    setShowEditModal(true);
  };
  
  const handleUpdateQuestion = async () => {
    if (!editingQuestion.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }
    
    if (editingQuestion.options.length !== 4 || editingQuestion.options.some(opt => !opt.trim())) {
      toast.error('Please provide exactly 4 non-empty options');
      return;
    }
    
    try {
      await axios.put(
        `${API}/admin/question-pool/${editingQuestion.id}`,
        editingQuestion,
        { withCredentials: true }
      );
      toast.success('Question updated successfully');
      setShowEditModal(false);
      setEditingQuestion(null);
      loadQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update question');
    }
  };

  const toggleQuestionSelection = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const selectAllQuestions = () => {
    if (selectedQuestions.length === filteredQuestions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(filteredQuestions.map(q => q.id));
    }
  };

  const handleAddQuestionsToTest = async () => {
    if (!selectedTestId) {
      toast.error('Please select a test');
      return;
    }
    
    if (selectedQuestions.length === 0) {
      toast.error('Please select at least one question');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/add-questions-to-test/${selectedTestId}`,
        selectedQuestions,
        { withCredentials: true }
      );
      toast.success(`Added ${selectedQuestions.length} questions to test!`);
      setShowAddToTestModal(false);
      setSelectedQuestions([]);
      setSelectedTestId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add questions to test');
    }
  };

  const handleCreateTest = async (e) => {
    e.preventDefault();
    
    const time_limit_seconds = ['comps_ready', 'advanced'].includes(testForm.tier)
      ? Math.round(testForm.question_count * 1.25)
      : null;

    const testData = {
      ...testForm,
      time_limit_seconds
    };

    try {
      await axios.post(`${API}/admin/tests`, testData, {
        withCredentials: true
      });
      toast.success('Test created! Go to Test Bank to manage it.');
      setShowTestForm(false);
      setTestForm({ title: '', description: '', tier: 'basic', category: 'acupuncture', question_count: 10, passing_score: 70 });
      loadTests();
    } catch (error) {
      toast.error('Failed to create test');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title text-[#2C1810]">Question Bank</h2>
        <div className="flex gap-3">
          <button
            onClick={() => window.location.href = '/admin/quiz-management'}
            className="retro-button-small flex items-center gap-2"
          >
            Manage Quizzes
          </button>
          <button
            onClick={() => setShowTestForm(true)}
            className="retro-button flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create New Test
          </button>
        </div>
      </div>

      {/* View Switcher */}
      <div className="flex gap-3 flex-wrap">
        <button
          onClick={() => setActiveView('pool')}
          className={`retro-button-small flex items-center gap-2 ${activeView === 'pool' ? 'bg-[#6B4423] text-[#F4E4C1]' : ''}`}
        >
          <FileText className="w-4 h-4" />
          Question Pool
        </button>
        <button
          onClick={() => setActiveView('ai-content')}
          className={`retro-button-small flex items-center gap-2 ${activeView === 'ai-content' ? 'bg-[#6B4423] text-[#F4E4C1]' : ''}`}
        >
          <Brain className="w-4 h-4" />
          Generate from Content
        </button>
        <button
          onClick={() => setActiveView('ai-prompt')}
          className={`retro-button-small flex items-center gap-2 ${activeView === 'ai-prompt' ? 'bg-[#6B4423] text-[#F4E4C1]' : ''}`}
        >
          <Wand2 className="w-4 h-4" />
          Generate from Topic
        </button>
        <button
          onClick={() => setActiveView('ai-image')}
          className={`retro-button-small flex items-center gap-2 ${activeView === 'ai-image' ? 'bg-[#6B4423] text-[#F4E4C1]' : ''}`}
        >
          <Image className="w-4 h-4" />
          Generate from Image
        </button>
        <button
          onClick={() => setActiveView('ai-document')}
          data-testid="qb-tab-document"
          className={`retro-button-small flex items-center gap-2 ${activeView === 'ai-document' ? 'bg-[#6B4423] text-[#F4E4C1]' : ''}`}
        >
          <FileUp className="w-4 h-4" />
          Generate from Document
        </button>
      </div>

      {/* Question Pool View */}
      {activeView === 'pool' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="cassette-card p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">
                  <Search className="w-4 h-4 inline mr-1" />
                  Search Questions
                </label>
                <input
                  type="text"
                  placeholder="Search by text or tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Tier</label>
                <select
                  value={tierFilter}
                  onChange={(e) => {
                    setTierFilter(e.target.value);
                    setSectionFilter('All'); // Reset section when tier changes
                  }}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  <option value="All">All Tiers</option>
                  {TIERS.filter(t => t !== 'All').map(tier => (
                    <option key={tier} value={tier}>{TIER_LABELS[tier]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Section</label>
                <select
                  value={sectionFilter}
                  onChange={(e) => setSectionFilter(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  <option value="All">All Sections</option>
                  {tierFilter !== 'All' ? (
                    // Show sections for selected tier
                    categoriesByTier[tierFilter]?.map(section => (
                      <option key={section.value} value={section.value}>{section.label}</option>
                    ))
                  ) : (
                    // Show all sections organized by tier
                    <>
                      <optgroup label="Basic Practice">
                        {categoriesByTier.basic?.map(section => (
                          <option key={section.value} value={section.value}>{section.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Comps Ready">
                        {categoriesByTier.comps_ready?.map(section => (
                          <option key={section.value} value={section.value}>{section.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Advanced">
                        {categoriesByTier.advanced?.map(section => (
                          <option key={section.value} value={section.value}>{section.label}</option>
                        ))}
                      </optgroup>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Source</label>
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {SOURCES.map(source => (
                    <option key={source} value={source}>
                      {source === 'All' ? 'All Sources' : 
                       source === 'manual' ? 'Manual Entry' :
                       source === 'ai_content' ? 'AI Content' : 
                       source === 'ai_prompt' ? 'AI Topic' :
                       source === 'ai_image' ? 'AI Image' :
                       source === 'ai_document' ? 'AI Document' : source}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Created</label>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  <option value="All">All Dates</option>
                  <option value="Today">Today</option>
                  <option value="Yesterday">Yesterday</option>
                  <option value="This Week">This Week</option>
                  <option value="This Month">This Month</option>
                </select>
              </div>
            </div>

            <div className="mt-4 text-sm text-[#6B4423]">
              Showing {filteredQuestions.length} of {questions.length} questions
            </div>
          </div>

          {/* Selection Toolbar */}
          {filteredQuestions.length > 0 && (
            <div className="cassette-card p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button
                    onClick={selectAllQuestions}
                    className="retro-button-small"
                  >
                    {selectedQuestions.length === filteredQuestions.length ? 'Deselect All' : 'Select All'}
                  </button>
                  {selectedQuestions.length > 0 && (
                    <span className="text-[#6B4423]">
                      {selectedQuestions.length} question{selectedQuestions.length !== 1 ? 's' : ''} selected
                    </span>
                  )}
                </div>
                {selectedQuestions.length > 0 && (
                  <button
                    onClick={() => setShowAddToTestModal(true)}
                    className="retro-button flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add to Test
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Questions List */}
          <div className="space-y-4">
            {filteredQuestions.length === 0 ? (
              <div className="cassette-card p-12 text-center">
                <Brain className="w-16 h-16 text-[#6B4423] mx-auto mb-4" />
                <p className="text-[#3D2817] text-xl">No questions found</p>
                <p className="text-[#6B4423] mt-2">Use the question generators to create questions!</p>
              </div>
            ) : (
              filteredQuestions.map(question => (
                <div key={question.id} className="cassette-card p-6">
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={selectedQuestions.includes(question.id)}
                      onChange={() => toggleQuestionSelection(question.id)}
                      className="mt-1 w-5 h-5 cursor-pointer"
                    />
                    
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="px-3 py-1 bg-[#E8934D] text-white text-xs font-bold rounded">
                              {question.level}
                            </span>
                            <span className="px-3 py-1 bg-[#4D9E7C] text-white text-xs font-bold rounded capitalize">
                              {getSectionLabelFromSlug(question.category)}
                            </span>
                          </div>
                      
                          <p className="text-[#2C1810] mb-3">{question.question_text}</p>
                      
                          {question.tags && question.tags.length > 0 && (
                            <div className="flex items-center gap-2 mb-2">
                              <Tags className="w-4 h-4 text-[#6B4423]" />
                              <div className="flex flex-wrap gap-2">
                                {question.tags.map((tag, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-[#F4E4C1] text-[#6B4423] text-xs rounded">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditQuestion(question)}
                            className="p-2 hover:bg-blue-200 rounded"
                            title="Edit Question"
                          >
                            <Edit className="w-5 h-5 text-blue-600" />
                          </button>
                          <button
                            onClick={() => setExpandedQuestion(expandedQuestion === question.id ? null : question.id)}
                            className="p-2 hover:bg-[#E8B67A] rounded"
                            title="View Details"
                          >
                            {expandedQuestion === question.id ? (
                              <ChevronUp className="w-5 h-5 text-[#2C1810]" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-[#2C1810]" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="p-2 hover:bg-red-200 rounded"
                          >
                            <Trash2 className="w-5 h-5 text-red-600" />
                          </button>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedQuestion === question.id && (
                        <div className="mt-4 pt-4 border-t-2 border-[#E8B67A]">
                          <h4 className="text-sm font-bold text-[#2C1810] mb-3">Answer Options:</h4>
                          <div className="space-y-2 mb-4">
                            {question.options.map((option, idx) => (
                              <div key={idx} className={`p-3 rounded ${idx === question.correct_answer_index ? 'bg-green-100 border-2 border-green-500' : 'bg-[#F4E4C1]'}`}>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-[#2C1810]">{String.fromCharCode(65 + idx)}.</span>
                                  <span className="text-[#2C1810]">{option}</span>
                                  {idx === question.correct_answer_index && (
                                    <span className="ml-auto text-green-600 font-bold">✓ Correct</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          {question.explanation && (
                            <div className="mb-4">
                              <h5 className="text-sm font-bold text-[#2C1810] mb-2">Explanation:</h5>
                              <p className="text-sm text-[#6B4423] bg-[#F4E4C1] p-3 rounded">{question.explanation}</p>
                            </div>
                          )}

                          <div className="text-xs text-[#6B4423]">
                            <p>Created: {question.created_at ? new Date(question.created_at).toLocaleDateString() : 'N/A'}</p>
                            {question.updated_at && question.updated_at !== question.created_at && (
                              <p>Updated: {new Date(question.updated_at).toLocaleDateString()}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Content-Based Question Generator */}
      {activeView === 'ai-content' && (
        <div className="cassette-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-purple-500 rounded-lg">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl retro-title text-[#2C1810]">Generate Questions from Content</h3>
              <p className="text-sm text-[#6B4423]">Paste educational content to automatically extract quiz questions</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Educational Content *</label>
              <textarea
                value={contentWizard.content}
                onChange={(e) => setContentWizard({ ...contentWizard, content: e.target.value })}
                rows={8}
                placeholder="Paste textbook content, lecture notes, or study material here..."
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Citation (Optional)</label>
              <input
                type="text"
                value={contentWizard.citation}
                onChange={(e) => setContentWizard({ ...contentWizard, citation: e.target.value })}
                placeholder="e.g., Smith, J. (2024). Acupuncture Fundamentals, p. 42"
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
              />
              <p className="text-xs text-[#6B4423] mt-1">Add a source reference that will appear as a tag on generated questions</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Guidance / Instructions (Optional)</label>
              <textarea
                value={contentWizard.guidance}
                onChange={(e) => setContentWizard({ ...contentWizard, guidance: e.target.value })}
                rows={2}
                placeholder="Direct the AI, e.g. 'Focus on point locations & indications', 'Board-exam style', 'Emphasize contraindications'"
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                data-testid="qb-content-guidance"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Tier *</label>
                <select
                  value={contentWizard.tier}
                  onChange={(e) => {
                    const newTier = e.target.value;
                    const firstSection = categoriesByTier[newTier]?.[0]?.value || '';
                    setContentWizard({ ...contentWizard, tier: newTier, section: firstSection });
                  }}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {TIERS.filter(t => t !== 'All').map(tier => (
                    <option key={tier} value={tier}>{TIER_LABELS[tier]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Section *</label>
                <select
                  value={contentWizard.section}
                  onChange={(e) => setContentWizard({ ...contentWizard, section: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {categoriesByTier[contentWizard.tier]?.length > 0 ? (
                    categoriesByTier[contentWizard.tier].map(section => (
                      <option key={section.value} value={section.value}>{section.label}</option>
                    ))
                  ) : (
                    <option value="">No sections available</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Provider *</label>
                <select
                  value={contentWizard.provider}
                  onChange={(e) => {
                    const provider = AI_PROVIDERS.find(p => p.value === e.target.value);
                    setContentWizard({ ...contentWizard, provider: e.target.value, model: provider.models[0] });
                  }}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {AI_PROVIDERS.map(provider => (
                    <option key={provider.value} value={provider.value}>{provider.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Model *</label>
                <select
                  value={contentWizard.model}
                  onChange={(e) => setContentWizard({ ...contentWizard, model: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {AI_PROVIDERS.find(p => p.value === contentWizard.provider)?.models.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="w-full md:w-1/4">
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Number of Questions</label>
              <input
                type="number"
                value={contentWizard.num_questions}
                onChange={(e) => setContentWizard({ ...contentWizard, num_questions: parseInt(e.target.value) })}
                min="1"
                max="20"
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
              />
            </div>

            <button
              onClick={handleAIGenerateFromContent}
              disabled={aiGenerating}
              className="retro-button w-full flex items-center justify-center space-x-2 bg-purple-600 text-white"
            >
              <Sparkles className={`w-5 h-5 ${aiGenerating ? 'animate-spin' : ''}`} />
              <span>{aiGenerating ? 'Generating... (this may take 30-60 seconds)' : 'Generate Questions'}</span>
            </button>
            {aiGenerating && (
              <p className="text-sm text-[#6B4423] mt-2 text-center">
                ✨ Creating quality questions from your content. Please wait...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Topic-Based Question Generator */}
      {activeView === 'ai-prompt' && (
        <div className="cassette-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-500 rounded-lg">
              <Wand2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl retro-title text-[#2C1810]">Generate Questions from Topic</h3>
              <p className="text-sm text-[#6B4423]">Enter a topic to automatically create questions or a full quiz</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Topic / Prompt *</label>
              <textarea
                value={promptWizard.prompt}
                onChange={(e) => setPromptWizard({ ...promptWizard, prompt: e.target.value })}
                rows={4}
                placeholder="Example: 'Generate questions about the 12 primary meridians' or 'Create a quiz on TCM diagnostic methods'"
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Guidance / Instructions (Optional)</label>
              <textarea
                value={promptWizard.guidance}
                onChange={(e) => setPromptWizard({ ...promptWizard, guidance: e.target.value })}
                rows={2}
                placeholder="Direct the AI, e.g. 'Board-exam style', 'Emphasize differentiation', 'Include clinical scenarios'"
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                data-testid="qb-prompt-guidance"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Tier *</label>
                <select
                  value={promptWizard.tier}
                  onChange={(e) => {
                    const newTier = e.target.value;
                    const firstSection = categoriesByTier[newTier]?.[0]?.value || '';
                    setPromptWizard({ ...promptWizard, tier: newTier, section: firstSection });
                  }}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {TIERS.filter(t => t !== 'All').map(tier => (
                    <option key={tier} value={tier}>{TIER_LABELS[tier]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Section *</label>
                <select
                  value={promptWizard.section}
                  onChange={(e) => setPromptWizard({ ...promptWizard, section: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {categoriesByTier[promptWizard.tier]?.length > 0 ? (
                    categoriesByTier[promptWizard.tier].map(section => (
                      <option key={section.value} value={section.value}>{section.label}</option>
                    ))
                  ) : (
                    <option value="">No sections available</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Provider *</label>
                <select
                  value={promptWizard.provider}
                  onChange={(e) => {
                    const provider = AI_PROVIDERS.find(p => p.value === e.target.value);
                    setPromptWizard({ ...promptWizard, provider: e.target.value, model: provider.models[0] });
                  }}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {AI_PROVIDERS.map(provider => (
                    <option key={provider.value} value={provider.value}>{provider.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Model *</label>
                <select
                  value={promptWizard.model}
                  onChange={(e) => setPromptWizard({ ...promptWizard, model: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {AI_PROVIDERS.find(p => p.value === promptWizard.provider)?.models.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Number of Questions</label>
                <input
                  type="number"
                  value={promptWizard.num_questions}
                  onChange={(e) => setPromptWizard({ ...promptWizard, num_questions: parseInt(e.target.value) })}
                  min="1"
                  max="20"
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center space-x-2 text-[#2C1810] font-bold">
                  <input
                    type="checkbox"
                    checked={promptWizard.quiz_type === 'full_quiz'}
                    onChange={(e) => setPromptWizard({ ...promptWizard, quiz_type: e.target.checked ? 'full_quiz' : 'questions' })}
                    className="w-5 h-5"
                  />
                  <span>Generate Full Quiz</span>
                </label>
              </div>
            </div>

            <button
              onClick={handleAIGenerateFromPrompt}
              disabled={aiGenerating}
              className="retro-button w-full flex items-center justify-center space-x-2 bg-blue-600 text-white"
            >
              <Sparkles className={`w-5 h-5 ${aiGenerating ? 'animate-spin' : ''}`} />
              <span>{aiGenerating ? 'Generating... (this may take 30-60 seconds)' : 'Generate Questions'}</span>
            </button>
            {aiGenerating && (
              <p className="text-sm text-[#6B4423] mt-2 text-center">
                ✨ Creating quality questions on your topic. Please wait...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Image-Based Question Generator */}
      {activeView === 'ai-image' && (
        <div className="cassette-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-500 rounded-lg">
              <Image className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl retro-title text-[#2C1810]">Generate Questions from Image</h3>
              <p className="text-sm text-[#6B4423]">Upload an image to automatically generate quiz questions from visual content</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Image Upload Section */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Upload or Paste Image *</label>
              
              {!imagePreview ? (
                <div 
                  className="border-2 border-dashed border-[#6B4423] rounded-lg p-8 text-center"
                  onPaste={handleImagePaste}
                  tabIndex={0}
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleImageSelect}
                    disabled={imageUploading}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer flex flex-col items-center gap-3"
                  >
                    {imageUploading ? (
                      <>
                        <Sparkles className="w-12 h-12 text-[#6B4423] animate-spin" />
                        <p className="text-[#6B4423] font-semibold">Uploading image...</p>
                        <p className="text-sm text-[#8B5A3C]">Please wait while we prepare your image</p>
                      </>
                    ) : (
                      <>
                        <Image className="w-12 h-12 text-[#6B4423]" />
                        <p className="text-[#6B4423] font-semibold">Click to upload or paste from clipboard</p>
                        <p className="text-sm text-[#8B5A3C]">PNG, JPG, JPEG, or WebP (max 10MB)</p>
                        <p className="text-xs text-[#8B5A3C] mt-2">💡 Tip: Copy an image and paste it here (Ctrl/Cmd+V)</p>
                      </>
                    )}
                  </label>
                </div>
              ) : (
                <div className="border-2 border-[#4D9E7C] rounded-lg p-4 bg-green-50">
                  <div className="flex items-start gap-4">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded border-2 border-[#6B4423]"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded">
                          ✓ Image Ready
                        </span>
                        <button
                          onClick={handleClearImage}
                          className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded hover:bg-red-600"
                        >
                          Remove
                        </button>
                      </div>
                      <p className="text-sm text-[#6B4423]">
                        Image uploaded successfully. Configure settings below and click &quot;Generate Questions&quot;.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Tier *</label>
                <select
                  value={imageWizard.tier}
                  onChange={(e) => {
                    const newTier = e.target.value;
                    const firstSection = categoriesByTier[newTier]?.[0]?.value || '';
                    setImageWizard({ ...imageWizard, tier: newTier, section: firstSection });
                  }}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {TIERS.filter(t => t !== 'All').map(tier => (
                    <option key={tier} value={tier}>{TIER_LABELS[tier]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Section *</label>
                <select
                  value={imageWizard.section}
                  onChange={(e) => setImageWizard({ ...imageWizard, section: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {categoriesByTier[imageWizard.tier]?.length > 0 ? (
                    categoriesByTier[imageWizard.tier].map(section => (
                      <option key={section.value} value={section.value}>{section.label}</option>
                    ))
                  ) : (
                    <option value="">No sections available</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">
                  Model * 
                  <span className="text-xs ml-2 text-[#8B5A3C]">(Gemini Only)</span>
                </label>
                <select
                  value={imageWizard.model}
                  onChange={(e) => setImageWizard({ ...imageWizard, model: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  <option value="gemini-2.5-pro">gemini-2.5-pro</option>
                  <option value="gemini-2.0-flash">gemini-2.0-flash</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Number of Questions</label>
                <input
                  type="number"
                  value={imageWizard.num_questions}
                  onChange={(e) => setImageWizard({ ...imageWizard, num_questions: parseInt(e.target.value) })}
                  min="1"
                  max="20"
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>
            </div>

            <button
              onClick={handleAIGenerateFromImage}
              disabled={aiGenerating || !selectedImage}
              className="retro-button w-full flex items-center justify-center space-x-2 bg-green-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className={`w-5 h-5 ${aiGenerating ? 'animate-spin' : ''}`} />
              <span>{aiGenerating ? 'Analyzing image... (this may take 30-60 seconds)' : 'Generate Questions from Image'}</span>
            </button>
            {aiGenerating && (
              <p className="text-sm text-[#6B4423] mt-2 text-center">
                ✨ AI is analyzing the image and creating questions. Please wait...
              </p>
            )}
            {!selectedImage && (
              <p className="text-sm text-orange-600 mt-2 text-center">
                ⚠️ Please upload an image first before generating questions
              </p>
            )}
          </div>
        </div>
      )}

      {/* Document-Based Question Generator */}
      {activeView === 'ai-document' && (
        <div className="cassette-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-orange-500 rounded-lg">
              <FileUp className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl retro-title text-[#2C1810]">Generate Questions from a Study Guide</h3>
              <p className="text-sm text-[#6B4423]">Upload a .docx or .pdf — the whole document is read and questions are drawn across all sections</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Upload Document (.docx or .pdf) *</label>
              <input
                type="file"
                accept=".docx,.pdf"
                onChange={(e) => setSelectedDoc(e.target.files?.[0] || null)}
                className="hidden"
                id="qb-doc-upload"
                data-testid="qb-doc-upload-input"
              />
              <div className="border-2 border-dashed border-[#6B4423] rounded-lg p-6 text-center">
                <label htmlFor="qb-doc-upload" className="cursor-pointer flex flex-col items-center gap-2">
                  <FileUp className="w-10 h-10 text-[#6B4423]" />
                  <p className="text-[#6B4423] font-semibold">{selectedDoc ? selectedDoc.name : 'Click to choose a .docx or .pdf'}</p>
                  <p className="text-xs text-[#8B5A3C]">Text-based PDFs only (scanned PDFs need OCR) · up to 25MB</p>
                </label>
                {selectedDoc && (
                  <button type="button" onClick={() => setSelectedDoc(null)} className="mt-3 px-3 py-1 bg-red-500 text-white text-xs font-bold rounded hover:bg-red-600">Remove</button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Citation (Optional)</label>
              <input
                type="text"
                value={documentWizard.citation}
                onChange={(e) => setDocumentWizard({ ...documentWizard, citation: e.target.value })}
                placeholder="e.g., Krolick Study Guide, 2026"
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
              />
              <p className="text-xs text-[#6B4423] mt-1">Added as a tag on every generated question</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Guidance / Instructions (Optional)</label>
              <textarea
                value={documentWizard.guidance}
                onChange={(e) => setDocumentWizard({ ...documentWizard, guidance: e.target.value })}
                rows={2}
                placeholder="Direct the AI, e.g. 'Focus on point indications', 'Board-exam style', 'Emphasize contraindications'"
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                data-testid="qb-document-guidance"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Tier *</label>
                <select
                  value={documentWizard.tier}
                  onChange={(e) => {
                    const newTier = e.target.value;
                    const firstSection = categoriesByTier[newTier]?.[0]?.value || '';
                    setDocumentWizard({ ...documentWizard, tier: newTier, section: firstSection });
                  }}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {TIERS.filter(t => t !== 'All').map(tier => (
                    <option key={tier} value={tier}>{TIER_LABELS[tier]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Section *</label>
                <select
                  value={documentWizard.section}
                  data-testid="qb-document-section"
                  onChange={(e) => setDocumentWizard({ ...documentWizard, section: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {categoriesByTier[documentWizard.tier]?.length > 0 ? (
                    categoriesByTier[documentWizard.tier].map(section => (
                      <option key={section.value} value={section.value}>{section.label}</option>
                    ))
                  ) : (
                    <option value="">No sections available</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Provider *</label>
                <select
                  value={documentWizard.provider}
                  data-testid="qb-document-provider"
                  onChange={(e) => {
                    const provider = AI_PROVIDERS.find(p => p.value === e.target.value);
                    setDocumentWizard({ ...documentWizard, provider: e.target.value, model: provider.models[0] });
                  }}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {AI_PROVIDERS.map(provider => (
                    <option key={provider.value} value={provider.value}>{provider.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Model *</label>
                <select
                  value={documentWizard.model}
                  data-testid="qb-document-model"
                  onChange={(e) => setDocumentWizard({ ...documentWizard, model: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  {AI_PROVIDERS.find(p => p.value === documentWizard.provider)?.models.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Question Count Mode</label>
                <select
                  value={documentWizard.count_mode}
                  onChange={(e) => setDocumentWizard({ ...documentWizard, count_mode: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  data-testid="qb-document-count-mode"
                >
                  <option value="total">Total across document</option>
                  <option value="per_section">Per section</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">
                  {documentWizard.count_mode === 'per_section' ? 'Questions per section' : 'Total questions across document'}
                </label>
                <input
                  type="number"
                  value={documentWizard.num_questions}
                  data-testid="qb-document-num-questions"
                  onChange={(e) => setDocumentWizard({ ...documentWizard, num_questions: parseInt(e.target.value) })}
                  min="1"
                  max="50"
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>
            </div>

            <div className="border-2 border-[#6B4423] rounded-lg p-4 bg-[#F4E4C1]/60">
              <label className="flex items-center gap-2 font-bold text-[#2C1810] cursor-pointer">
                <input
                  type="checkbox"
                  checked={documentWizard.create_test}
                  onChange={(e) => setDocumentWizard({ ...documentWizard, create_test: e.target.checked })}
                  className="w-5 h-5"
                  data-testid="qb-document-create-test"
                />
                <span>Also build a ready-to-use test from these questions (one-click)</span>
              </label>
              {documentWizard.create_test && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#6B4423] mb-2">Test Title *</label>
                    <input
                      type="text"
                      value={documentWizard.test_title}
                      onChange={(e) => setDocumentWizard({ ...documentWizard, test_title: e.target.value })}
                      placeholder="e.g., Lung Channel & Wind-Cold Exam"
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                      data-testid="qb-document-test-title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#6B4423] mb-2">Passing Score (%)</label>
                    <input
                      type="number"
                      value={documentWizard.passing_score}
                      onChange={(e) => setDocumentWizard({ ...documentWizard, passing_score: parseInt(e.target.value) })}
                      min="0"
                      max="100"
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                    />
                  </div>
                </div>
              )}
              <p className="text-xs text-[#6B4423] mt-2">The test is created in your Test Bank, where you can then generate flashcards, games, and other study materials.</p>
            </div>

            <button
              onClick={handleAIGenerateFromDocument}
              disabled={aiGenerating || !selectedDoc}
              data-testid="qb-document-generate"
              className="retro-button w-full flex items-center justify-center space-x-2 bg-orange-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className={`w-5 h-5 ${aiGenerating ? 'animate-spin' : ''}`} />
              <span>{aiGenerating ? 'Reading document & generating... (may take 1-2 minutes)' : (documentWizard.create_test ? 'Generate Questions & Build Test' : 'Generate Questions from Document')}</span>
            </button>
            {aiGenerating && docJob && (
              <div className="mt-3" data-testid="qb-document-progress">
                <div className="flex justify-between text-sm text-[#6B4423] mb-1">
                  <span>{docJob.total > 0 ? `Processing section ${docJob.processed} of ${docJob.total}…` : 'Reading your study guide…'}</span>
                  <span>{docJob.total > 0 ? `${Math.round((docJob.processed / docJob.total) * 100)}%` : ''}</span>
                </div>
                <div className="w-full bg-[#E8D5B7] rounded-full h-3 overflow-hidden border border-[#6B4423]">
                  <div
                    className="bg-orange-600 h-3 transition-all duration-500"
                    style={{ width: docJob.total > 0 ? `${(docJob.processed / docJob.total) * 100}%` : '8%' }}
                  ></div>
                </div>
              </div>
            )}
            {!selectedDoc && (
              <p className="text-sm text-orange-600 mt-2 text-center">
                ⚠️ Please choose a .docx or .pdf file first
              </p>
            )}
          </div>
        </div>
      )}

      {/* Create Test Modal */}
      {showTestForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="cassette-card p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl retro-title text-[#2C1810]">Create New Test</h3>
              <button onClick={() => setShowTestForm(false)} className="text-[#2C1810]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateTest} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Test Title *</label>
                <input
                  type="text"
                  value={testForm.title}
                  onChange={(e) => setTestForm({ ...testForm, title: e.target.value })}
                  required
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description</label>
                <textarea
                  value={testForm.description}
                  onChange={(e) => setTestForm({ ...testForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Category *</label>
                  <select
                    value={testForm.category}
                    onChange={(e) => setTestForm({ ...testForm, category: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  >
                    <option value="acupuncture">Acupuncture</option>
                    <option value="biomedicine">Biomedicine</option>
                    <option value="foundations">Foundations</option>
                    <option value="herbs">Herbs</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Tier *</label>
                  <select
                    value={testForm.tier}
                    onChange={(e) => setTestForm({ ...testForm, tier: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  >
                    <option value="basic">Free</option>
                    <option value="comps_ready">Comps Ready</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Question Count *</label>
                  <input
                    type="number"
                    value={testForm.question_count}
                    onChange={(e) => setTestForm({ ...testForm, question_count: parseInt(e.target.value) })}
                    min="1"
                    required
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Passing Score (%) *</label>
                  <input
                    type="number"
                    value={testForm.passing_score}
                    onChange={(e) => setTestForm({ ...testForm, passing_score: parseInt(e.target.value) })}
                    min="0"
                    max="100"
                    required
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button type="submit" className="retro-button flex-1">
                  Create Test
                </button>
                <button
                  type="button"
                  onClick={() => setShowTestForm(false)}
                  className="retro-button-small flex-1 bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Questions to Test Modal */}
      {showAddToTestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="cassette-card p-6 max-w-lg w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl retro-title text-[#2C1810]">
                Add {selectedQuestions.length} Questions to Test
              </h3>
              <button onClick={() => setShowAddToTestModal(false)} className="text-[#2C1810]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Select Test *</label>
                <select
                  value={selectedTestId}
                  onChange={(e) => setSelectedTestId(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                >
                  <option value="">Choose a test...</option>
                  {tests.map(test => (
                    <option key={test.id} value={test.id}>
                      {test.title} ({test.tier} - {test.category})
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-sm text-[#6B4423] bg-[#F4E4C1] p-3 rounded">
                💡 Questions will be added to the end of the test. You can reorder them later in Test Bank.
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleAddQuestionsToTest}
                  className="retro-button flex-1"
                >
                  Add to Test
                </button>
                <button
                  onClick={() => setShowAddToTestModal(false)}
                  className="retro-button-small flex-1 bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Question Modal */}
      {showEditModal && editingQuestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="cassette-card p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl retro-title text-[#2C1810]">Edit Question</h3>
              <button onClick={() => setShowEditModal(false)} className="text-[#2C1810]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Question Text */}
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Question Text *</label>
                <textarea
                  value={editingQuestion.question_text}
                  onChange={(e) => setEditingQuestion({...editingQuestion, question_text: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>

              {/* Options */}
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Answer Options *</label>
                {editingQuestion.options.map((option, idx) => (
                  <div key={idx} className="mb-2">
                    <div className="flex gap-2">
                      <input
                        type="radio"
                        name="correct_answer"
                        checked={editingQuestion.correct_answer_index === idx}
                        onChange={() => setEditingQuestion({...editingQuestion, correct_answer_index: idx})}
                        className="mt-3"
                      />
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...editingQuestion.options];
                          newOptions[idx] = e.target.value;
                          setEditingQuestion({...editingQuestion, options: newOptions});
                        }}
                        placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                        className="flex-1 px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                      />
                    </div>
                  </div>
                ))}
                <p className="text-xs text-[#6B4423] mt-2">Select the radio button for the correct answer</p>
              </div>

              {/* Explanation */}
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Explanation (Optional)</label>
                <textarea
                  value={editingQuestion.explanation || ''}
                  onChange={(e) => setEditingQuestion({...editingQuestion, explanation: e.target.value})}
                  rows={2}
                  placeholder="Explain why this answer is correct..."
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>

              {/* Tags/Citation */}
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Tags / Citations</label>
                <input
                  type="text"
                  value={editingQuestion.tags.join(', ')}
                  onChange={(e) => setEditingQuestion({
                    ...editingQuestion, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                  })}
                  placeholder="tag1, tag2, citation..."
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
                <p className="text-xs text-[#6B4423] mt-1">Separate multiple tags with commas</p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleUpdateQuestion}
                  className="retro-button flex-1"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="retro-button-outline flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
